import React, { useState } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImportAllProducts } from "../../../../services/ProductService/AllProductList/AllProductServices";

const BulkImportServiceModal = ({ closeModal, onSuccess }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Handle drag over
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0]; // Select only the first file
        handleFileSelection(file);
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0]; // Select only the first file
        handleFileSelection(file);
    };

    // Set selected file if valid
    const handleFileSelection = (file) => {
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            setMessage({ type: "error", text: "⚠️ File exceeds the 10 MB size limit." });
            return;
        }

        setSelectedFile(file);
        setMessage({ type: "", text: "" });
    };

    // Handle upload action
    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage({ type: "error", text: "⚠️ Please select a file." });
            return;
        }

        setIsLoading(true);
        setMessage({ type: "", text: "" });

        try {
            const formData = new FormData();
            formData.append("file", selectedFile); // Append a single file

            await ImportAllProducts("POST", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setMessage({ type: "success", text: "✅ File uploaded successfully!" });

            setTimeout(() => {
                onSuccess(); // Close modal after success
            }, 1500);
        } catch (error) {
            console.error("Upload failed:", error);
            setMessage({
                type: "error",
                text: error.response?.data?.message || "❌ Failed to upload file.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50"
            onClick={closeModal}
        >
            <div
                className="bg-white w-[500px] h-fit p-6 rounded-lg flex flex-col"
                onClick={(e) => e.stopPropagation()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                {/* Modal Header */}
                <h1 className="text-lg font-bold text-left text-gray-700">
                    Import Product Details
                </h1>

                {/* Drag and Drop Zone */}
                <div className="flex-grow flex flex-col justify-center items-center border-dashed border-2 border-gray-400 p-6 mt-4">
                    <FaCloudUploadAlt className="text-5xl text-gray-500 mb-4" />
                    <p className="text-gray-600">Select a file or drag and drop here</p>
                    <span className="text-sm text-gray-400 mt-1">
                        File size should not exceed 10 MB
                    </span>

                    {/* Hidden File Input */}
                    <input
                        type="file"
                        id="fileInput"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <label
                        htmlFor="fileInput"
                        className="mt-4 bg-[#0F91D2] text-white px-6 py-2 rounded-lg cursor-pointer"
                    >
                        Select File
                    </label>
                </div>

                {/* Selected File Display */}
                {selectedFile && (
                    <div className="mt-4 border border-gray-200 p-2 rounded text-sm text-gray-600 truncate">
                        {selectedFile.name}
                    </div>
                )}

                {/* Status Message */}
                {message.text && (
                    <div
                        className={`mt-2 text-sm ${
                            message.type === "success" ? "text-green-600" : "text-red-600"
                        }`}
                    >
                        {message.text}
                    </div>
                )}

                {/* Modal Actions */}
                <div className="mt-4 flex justify-end gap-4">
                    <button
                        className="bg-gray-300 px-4 py-2 rounded-lg"
                        onClick={closeModal}
                        disabled={isLoading}
                    >
                        Close
                    </button>
                    <button
                        className={`${
                            !selectedFile || isLoading
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-[#0F91D2] hover:bg-blue-700"
                        } text-white px-6 py-2 rounded-lg`}
                        onClick={handleUpload}
                        disabled={!selectedFile || isLoading}
                    >
                        {isLoading ? "Uploading..." : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkImportServiceModal;